import { observer } from "mobx-react";
import React, { useState } from "react";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { useStore } from "../../../models";
import { IRoom } from "../../../models/room/Room";
import { AgentButton } from "@fidget/common/components/inputs/buttons/agentButton/agentButton";
import { ScheduleButton } from "../scheduleButton";
import { IconClear, SearchingInput } from "../searchingInput/searchingInput";
import { MessageWidgetButton } from "./messageWidgetButton";
import styles from "./richMessages.css";
import { isContactEnquiryConfigContext } from "@fidget/common/models/shared/spinner/types/messageWidgets/WidgetFormSubmissionWithWidget";
import { partition } from "lodash";
import { getMessageWidgetIcon } from "./utils";

interface Props {
  room: IRoom;
}

export const RichMessages = observer(({ room }: Props) => {
  const { currentUserStore, callStore, cobrowsingStore, messageWidgetsStore } = useStore();
  const shouldShowCallProposal =
    currentUserStore.orgConfig !== undefined && !currentUserStore.orgConfig.callsDisabled;
  const shouldShowCobrowsingProposal =
    currentUserStore.orgConfig !== undefined && currentUserStore.orgConfig.cobrowseEnabled;
  const shouldShowContactEnquiry = !!currentUserStore.orgConfig?.contactEnquiryActionEnabled;
  const shouldShowScheduleMeeting = !!currentUserStore.orgConfig?.meetingActionEnabled;

  const callProposalDisabled =
    !callStore.isNoCall ||
    (cobrowsingStore.cobrowsingSessionAccepted && cobrowsingStore.cobrowsingSessionId !== room.id);
  const cobrowseProposalDisabled =
    cobrowsingStore.cobrowsingSessionAccepted ||
    (!callStore.isNoCall && callStore.remoteUserId !== room.owner.id);

  // can exist only one ContactEnquiryConfig per org per locale
  const [[contactEnquiryWidgetConfig], widgetConfigs] = partition(
    messageWidgetsStore
      .getMessageWidgets(room.languageOption, room.owner.tagGroupId)
      .filter((config) => config.availableForAgents),
    ({ config }) => isContactEnquiryConfigContext(config)
  );

  const [isUsingSearchBox, setIsUsingSearchBox] = useState(false);
  const [searchValue, setSearchValue] = useState<string>();

  const renderMessageWidgetsButtons = () =>
    widgetConfigs
      .filter((widgetConfig) => {
        if (searchValue) {
          return widgetConfig.name.toLowerCase().match(new RegExp(searchValue, "g"));
        }
        return true;
      })
      .map((widgetConfig) => (
        <MessageWidgetButton
          key={"message_widget" + widgetConfig.id}
          widgetId={widgetConfig.id}
          name={widgetConfig.name}
          icon={getMessageWidgetIcon(widgetConfig.config.type)}
          sendMessage={() => messageWidgetsStore.sendMessageWidgetForm(widgetConfig.id, room.id)}
        />
      ));

  const renderCallProposalButton = () => {
    return (
      <AgentButton
        id={"call_proposal_button"}
        icon="icon-call"
        fullWidth
        disabled={callProposalDisabled}
        onClick={() => {
          room.sendCallProposal();
        }}
      >
        <L10n id={"richMessages.callProposal"} />
      </AgentButton>
    );
  };

  const renderCobrowsingProposalButton = () => {
    return (
      <AgentButton
        id={"cobrowsing_proposal_button"}
        icon="icon-cobrowsing-new"
        fullWidth
        disabled={cobrowseProposalDisabled}
        onClick={() => {
          room.sendCobrowsingProposal();
        }}
      >
        <L10n id={"richMessages.cobrowsingProposal"} />
      </AgentButton>
    );
  };

  const renderContactEnquiryButton = () => {
    if (!contactEnquiryWidgetConfig) {
      return <></>;
    }
    return (
      <MessageWidgetButton
        key={"message_widget" + contactEnquiryWidgetConfig.id}
        widgetId={contactEnquiryWidgetConfig.id}
        name={intl.formatMessage("richMessages.infoCapture")}
        sendMessage={() =>
          messageWidgetsStore.sendMessageWidgetForm(contactEnquiryWidgetConfig.id, room.id)
        }
      />
    );
  };

  const renderMeetingScheduleButton = () => {
    return <ScheduleButton room={room} draggable icon={"icon-cal-icon"} />;
  };

  const renderSearchbox = () => {
    return (
      widgetConfigs.length > 0 && (
        <SearchingInput
          value={searchValue || ""}
          onChange={setSearchValue}
          onFocus={() => setIsUsingSearchBox(true)}
          onBlur={() => setIsUsingSearchBox(false)}
          placeholder={intl.formatMessage("richMessages.searchbox.placeholder")}
        >
          {!!searchValue && <IconClear onClick={() => setSearchValue(undefined)} />}
        </SearchingInput>
      )
    );
  };

  return (
    <div className={styles["rich-messages-container"]}>
      <div className={styles["messages-list"]}>
        {renderSearchbox()}
        {!isUsingSearchBox && !searchValue && (
          <>
            {shouldShowContactEnquiry && renderContactEnquiryButton()}
            {shouldShowScheduleMeeting && renderMeetingScheduleButton()}
            {shouldShowCallProposal && renderCallProposalButton()}
            {shouldShowCobrowsingProposal && renderCobrowsingProposalButton()}
          </>
        )}
        {renderMessageWidgetsButtons()}
      </div>
    </div>
  );
});
